import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Sun,
  Moon,
  Star,
  Circle,
  Cloud,
  Heart,
  Leaf,
  X,
  Trees,
  Sparkles,
  UtensilsCrossed,
  BedDouble,
  Volume2,
  VolumeX,
  Globe,
} from "lucide-react";
import PinPad from "../components/PinPad";

/* ----- Child mode kiosk -----
   The signature Child Interface — built for the child.
   - No reading required (every label is also spoken by the kiosk in the chosen language).
   - 5 big "Where I am" buttons map 1:1 onto AmbientState
   - Optional toggle to the 8 emotional signals ("How I feel").
   - Calm confirmation animation "Thank you, {name}".
   - Optional approval mode (parent/admin-toggled) — when on, the child's tap is
     gently shared with staff rather than immediately changing state.
   - Multilingual: en / es / fr / ar (with RTL flip for Arabic).
   - Designed for tablet or wall-mounted device. Big touch targets. */

const WHERE_OPTIONS = [
  { key: "yard", state: "learning", Icon: Trees, color: "#9CC4A4" },
  { key: "play", state: "playing", Icon: Sparkles, color: "#F4D88F" },
  { key: "eat", state: "eating", Icon: UtensilsCrossed, color: "#F4B68F" },
  { key: "rest", state: "resting", Icon: BedDouble, color: "#9BC0E5" },
  { key: "care", state: "safe", Icon: Heart, color: "#E7B7C8" },
];

const FEEL_OPTIONS = [
  { signal: "happy", icon: "sun", Icon: Sun, color: "#E6BFA6" },
  { signal: "calm", icon: "circle", Icon: Circle, color: "#9BA995" },
  { signal: "playful", icon: "star", Icon: Star, color: "#E6BFA6" },
  { signal: "sleepy", icon: "moon", Icon: Moon, color: "#8A9984" },
  { signal: "shy", icon: "cloud", Icon: Cloud, color: "#9BA995" },
  { signal: "curious", icon: "leaf", Icon: Leaf, color: "#8A9984" },
  { signal: "need_hug", icon: "heart", Icon: Heart, color: "#E6BFA6" },
  { signal: "i_am_here", icon: "spiral", Icon: Circle, color: "#9BA995" },
];

const TRANSLATIONS = {
  en: {
    where: { yard: "Yard", play: "Play", eat: "Eat", rest: "Rest", care: "In Care" },
    feel: {
      happy: "happy",
      calm: "calm",
      playful: "playful",
      sleepy: "sleepy",
      shy: "shy",
      curious: "curious",
      need_hug: "I want a hug",
      i_am_here: "I am here",
    },
    prompt: "Hi {name}! Where are you now?",
    feelPrompt: "Hi {name}! How are you feeling?",
    thanks: "Thank you, {name}.",
    shared: "We've shared this with your teacher.",
    again: "Tap again",
    switchToFeel: "How I feel",
    switchToWhere: "Where I am",
    weHeard: "We heard you.",
    waitingForTeacher: "Your teacher will gently confirm.",
    forChild: "For {name}",
    needCaregiver: "This kiosk needs a caregiver.",
    signInCaregiver: "Sign in as caregiver",
    didNotMatch: "That didn't match. Try again, gently.",
  },
  es: {
    where: { yard: "Patio", play: "Juego", eat: "Comer", rest: "Descansar", care: "Con mi maestra" },
    feel: {
      happy: "feliz",
      calm: "tranquilo",
      playful: "juguetón",
      sleepy: "con sueño",
      shy: "tímido",
      curious: "curioso",
      need_hug: "quiero un abrazo",
      i_am_here: "aquí estoy",
    },
    prompt: "¡Hola {name}! ¿Dónde estás ahora?",
    feelPrompt: "¡Hola {name}! ¿Cómo te sientes?",
    thanks: "Gracias, {name}.",
    shared: "Lo compartimos con tu maestra.",
    again: "Tocar de nuevo",
    switchToFeel: "Cómo me siento",
    switchToWhere: "Dónde estoy",
    weHeard: "Te escuchamos.",
    waitingForTeacher: "Tu maestra lo confirmará suavemente.",
    forChild: "Para {name}",
    needCaregiver: "Este kiosko necesita una maestra.",
    signInCaregiver: "Iniciar sesión como maestra",
    didNotMatch: "No coincidió. Inténtalo otra vez, suavemente.",
  },
  fr: {
    where: { yard: "Jardin", play: "Jeu", eat: "Manger", rest: "Repos", care: "Avec ma maîtresse" },
    feel: {
      happy: "heureux",
      calm: "calme",
      playful: "joueur",
      sleepy: "fatigué",
      shy: "timide",
      curious: "curieux",
      need_hug: "je veux un câlin",
      i_am_here: "je suis là",
    },
    prompt: "Bonjour {name} ! Où es-tu maintenant ?",
    feelPrompt: "Bonjour {name} ! Comment te sens-tu ?",
    thanks: "Merci, {name}.",
    shared: "Nous l'avons partagé avec ta maîtresse.",
    again: "Toucher à nouveau",
    switchToFeel: "Comment je me sens",
    switchToWhere: "Où je suis",
    weHeard: "Nous t'avons entendu.",
    waitingForTeacher: "Ta maîtresse va doucement confirmer.",
    forChild: "Pour {name}",
    needCaregiver: "Ce kiosque a besoin d'une maîtresse.",
    signInCaregiver: "Se connecter comme maîtresse",
    didNotMatch: "Cela ne correspondait pas. Essaie encore, doucement.",
  },
  ar: {
    where: { yard: "الحديقة", play: "لعب", eat: "تناول الطعام", rest: "راحة", care: "مع معلمتي" },
    feel: {
      happy: "سعيد",
      calm: "هادئ",
      playful: "مرح",
      sleepy: "نعسان",
      shy: "خجول",
      curious: "فضولي",
      need_hug: "أريد عناقاً",
      i_am_here: "أنا هنا",
    },
    prompt: "مرحباً {name}! أين أنت الآن؟",
    feelPrompt: "مرحباً {name}! كيف تشعر؟",
    thanks: "شكراً، {name}.",
    shared: "شاركنا هذا مع معلمتك.",
    again: "اضغط مرة أخرى",
    switchToFeel: "كيف أشعر",
    switchToWhere: "أين أنا",
    weHeard: "سمعناك.",
    waitingForTeacher: "ستؤكد معلمتك بهدوء.",
    forChild: "لـ {name}",
    needCaregiver: "هذا الجهاز يحتاج إلى معلمة.",
    signInCaregiver: "تسجيل الدخول كمعلمة",
    didNotMatch: "غير متطابق. حاول مرة أخرى بهدوء.",
  },
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
];

const VOICE_LANG = { en: "en-US", es: "es-ES", fr: "fr-FR", ar: "ar-SA" };

function speak(text, lang) {
  try {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = VOICE_LANG[lang] || "en-US";
    u.rate = 0.95;
    u.pitch = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {
    // quiet
  }
}

export default function ChildMode() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [confirm, setConfirm] = useState(null);  // { mode, key, label, Icon, color, pending }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [pinError, setPinError] = useState(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [view, setView] = useState("where");  // "where" | "feel"
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("alouvya.kiosk.lang") || "en";
    } catch {
      return "en";
    }
  });
  const [voiceOn, setVoiceOn] = useState(() => {
    try {
      return localStorage.getItem("alouvya.kiosk.voice") === "1";
    } catch {
      return false;
    }
  });
  const [langOpen, setLangOpen] = useState(false);

  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;
  const isRTL = lang === "ar";

  useEffect(() => {
    api
      .get(`/children/${childId}`)
      .then((r) => setChild(r.data))
      .catch((err) => {
        setError(err.response?.data?.detail || t.needCaregiver);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  useEffect(() => {
    try {
      localStorage.setItem("alouvya.kiosk.lang", lang);
    } catch {}
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem("alouvya.kiosk.voice", voiceOn ? "1" : "0");
    } catch {}
  }, [voiceOn]);

  const attemptExit = () => {
    if (child?.kiosk_pin_set) {
      setShowPin(true);
      setPinError(null);
    } else {
      navigate(-1);
    }
  };

  const verifyPin = async (pin) => {
    setPinBusy(true);
    setPinError(null);
    try {
      const r = await api.post(`/children/${childId}/kiosk-pin/verify`, { pin });
      if (r.data.verified) {
        navigate(-1);
      } else {
        setPinError(t.didNotMatch);
      }
    } catch (err) {
      setPinError(err.response?.data?.detail || t.didNotMatch);
    } finally {
      setPinBusy(false);
    }
  };

  const tapWhere = async (opt) => {
    if (busy) return;
    setBusy(true);
    const label = t.where[opt.key];
    if (voiceOn) speak(label, lang);
    try {
      const r = await api.post(`/children/${childId}/whereabouts`, {
        state: opt.state,
        lang,
      });
      const pending = !!r.data?.pending;
      setConfirm({
        mode: "where",
        key: opt.key,
        label,
        Icon: opt.Icon,
        color: opt.color,
        pending,
      });
      if (voiceOn) {
        // Wait briefly so "Yard" finishes before "Thank you" begins.
        setTimeout(() => speak(t.thanks.replace("{name}", child.name), lang), 600);
      }
      if (!pending && r.data?.child) setChild(r.data.child);
      setTimeout(() => setConfirm(null), 6000);
    } catch (err) {
      toast(err.response?.data?.detail || "Try again, gently.");
    } finally {
      setBusy(false);
    }
  };

  const tapFeel = async (opt) => {
    if (busy) return;
    setBusy(true);
    const label = t.feel[opt.signal];
    if (voiceOn) speak(label, lang);
    try {
      await api.post(`/children/${childId}/self-check-in`, {
        signal: opt.signal,
        icon: opt.icon,
      });
      setConfirm({
        mode: "feel",
        key: opt.signal,
        label,
        Icon: opt.Icon,
        color: opt.color,
        pending: false,
      });
      if (voiceOn) {
        setTimeout(() => speak(t.thanks.replace("{name}", child.name), lang), 600);
      }
      setTimeout(() => setConfirm(null), 6000);
    } catch (err) {
      toast(err.response?.data?.detail || "Try again, gently.");
    } finally {
      setBusy(false);
    }
  };

  const promptText = useMemo(() => {
    if (!child) return "";
    return (view === "where" ? t.prompt : t.feelPrompt).replace(
      "{name}",
      child.name
    );
  }, [child, view, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-8 bg-[#FBF9F6]">
        <div className="max-w-md text-center fade-up">
          <span className="eyebrow">A quiet pause</span>
          <h1 className="mt-6 font-serif text-4xl leading-tight">
            {t.needCaregiver}
          </h1>
          <p className="mt-6 text-[#6A6863] font-light">{error}</p>
          <button
            onClick={() => navigate("/signin")}
            data-testid="childmode-signin"
            className="btn-primary mt-10"
          >
            {t.signInCaregiver}
          </button>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center font-serif italic text-[#6A6863]">
        a quiet moment…
      </div>
    );
  }

  return (
    <div
      data-testid="child-mode"
      data-lang={lang}
      data-view={view}
      dir={isRTL ? "rtl" : "ltr"}
      className="fixed inset-0 z-50 bg-[#FBF9F6] overflow-auto pb-16"
    >
      {/* Top-right kiosk controls — small, never playful */}
      <div className="absolute top-5 right-5 flex items-center gap-1 z-10">
        <button
          type="button"
          onClick={() => setVoiceOn((v) => !v)}
          data-testid="child-mode-voice-toggle"
          aria-label={voiceOn ? "Voice on" : "Voice off"}
          aria-pressed={voiceOn}
          className={
            "p-3 rounded-full transition-colors " +
            (voiceOn
              ? "bg-[#1F3A2A] text-white"
              : "text-[#6A6863] hover:text-[#2C2B29]")
          }
        >
          {voiceOn ? (
            <Volume2 size={18} strokeWidth={1.5} />
          ) : (
            <VolumeX size={18} strokeWidth={1.5} />
          )}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setLangOpen((v) => !v)}
            data-testid="child-mode-lang-toggle"
            aria-label="Change language"
            className="p-3 rounded-full text-[#6A6863] hover:text-[#2C2B29] transition-colors flex items-center gap-1.5"
          >
            <Globe size={18} strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.18em] uppercase">
              {lang.toUpperCase()}
            </span>
          </button>
          {langOpen && (
            <div
              data-testid="child-mode-lang-menu"
              className="absolute top-full right-0 mt-2 bg-white rounded-2xl border hairline shadow-[0_8px_30px_rgb(44,43,41,0.08)] py-2 min-w-[140px]"
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setLangOpen(false);
                  }}
                  data-testid={`child-mode-lang-${l.code}`}
                  aria-pressed={lang === l.code}
                  className={
                    "block w-full text-start px-4 py-2 text-sm transition-colors " +
                    (lang === l.code
                      ? "text-[#1F3A2A] bg-[#E6EFE8]"
                      : "text-[#2C2B29] hover:bg-[#F2EFEA]/60")
                  }
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={attemptExit}
          data-testid="child-mode-exit"
          aria-label="Exit child mode"
          className="text-[#6A6863] hover:text-[#2C2B29] p-3 transition-colors"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      {showPin && (
        <div
          data-testid="child-mode-pin-overlay"
          className="fixed inset-0 z-[70] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6"
        >
          <div className="relative w-full max-w-sm bg-white rounded-3xl border hairline p-10 fade-up">
            <button
              type="button"
              onClick={() => setShowPin(false)}
              data-testid="child-mode-pin-cancel"
              aria-label="Cancel"
              className="absolute top-4 right-4 text-[#6A6863] hover:text-[#2C2B29] p-2 transition-colors"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            <PinPad busy={pinBusy} error={pinError} onComplete={verifyPin} />
          </div>
        </div>
      )}

      <main className="px-8 sm:px-16 pt-16 sm:pt-24 max-w-5xl mx-auto">
        <div className="text-center fade-up">
          <span className="eyebrow">
            {t.forChild.replace("{name}", child.name)}
          </span>
          <h1
            data-testid="child-mode-prompt"
            className="mt-6 sm:mt-8 font-serif text-5xl sm:text-7xl leading-[1.05] tracking-tight"
          >
            {promptText}
          </h1>
        </div>

        {confirm ? (
          <div
            data-testid="child-mode-thanks"
            data-pending={confirm.pending ? "true" : "false"}
            className="mt-16 sm:mt-20 flex flex-col items-center text-center fade-up"
          >
            <div
              className="h-32 w-32 sm:h-44 sm:w-44 rounded-full flex items-center justify-center thanks-pulse"
              style={{ background: confirm.color }}
              aria-hidden
            >
              <confirm.Icon size={64} strokeWidth={1.2} className="text-white" />
            </div>
            <p
              data-testid="child-mode-thanks-line"
              className="mt-10 sm:mt-12 font-serif text-3xl sm:text-4xl italic text-[#2C2B29]"
            >
              {t.thanks.replace("{name}", child.name)}
            </p>
            <p
              data-testid="child-mode-thanks-sub"
              className="mt-3 text-[#6A6863] font-light text-base sm:text-lg"
            >
              {confirm.pending ? t.waitingForTeacher : t.weHeard}
            </p>
            <div className="mt-10">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                data-testid="child-mode-again"
                className="btn-ghost"
              >
                {t.again}
              </button>
            </div>
          </div>
        ) : view === "where" ? (
          <div
            data-testid="child-mode-where-grid"
            className="mt-12 sm:mt-16 grid grid-cols-2 sm:grid-cols-5 gap-5 sm:gap-7"
          >
            {WHERE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={busy}
                onClick={() => tapWhere(opt)}
                data-testid={`child-mode-where-${opt.key}`}
                className="group flex flex-col items-center gap-4 p-5 sm:p-7 rounded-3xl bg-white border hairline transition-all duration-500 hover:shadow-[0_8px_30px_rgb(44,43,41,0.06)] active:scale-[0.97]"
              >
                <div
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105"
                  style={{ background: opt.color }}
                  aria-hidden
                >
                  <opt.Icon size={48} strokeWidth={1.2} className="text-white" />
                </div>
                <span className="font-serif text-xl sm:text-2xl text-[#2C2B29] tracking-tight">
                  {t.where[opt.key]}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div
            data-testid="child-mode-feel-grid"
            className="mt-12 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-7"
          >
            {FEEL_OPTIONS.map((opt) => (
              <button
                key={opt.signal}
                type="button"
                disabled={busy}
                onClick={() => tapFeel(opt)}
                data-testid={`child-mode-${opt.signal}`}
                className="group flex flex-col items-center gap-4 p-5 sm:p-7 rounded-3xl bg-white border hairline transition-all duration-500 hover:shadow-[0_8px_30px_rgb(44,43,41,0.06)] active:scale-[0.97]"
              >
                <div
                  className="h-20 w-20 sm:h-24 sm:w-24 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105"
                  style={{ background: opt.color }}
                  aria-hidden
                >
                  <opt.Icon size={42} strokeWidth={1.2} className="text-white" />
                </div>
                <span className="font-serif text-lg sm:text-xl text-[#2C2B29] tracking-tight">
                  {t.feel[opt.signal]}
                </span>
              </button>
            ))}
          </div>
        )}

        {!confirm && (
          <div className="mt-14 sm:mt-16 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setView(view === "where" ? "feel" : "where")}
              data-testid="child-mode-view-toggle"
              className="btn-ghost"
            >
              {view === "where" ? t.switchToFeel : t.switchToWhere}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
